import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEmailMarketingCampaigns } from 'shared/services/email-marketing-campaign.service';
import {
  sendPaymentLinksByIdentifiers,
  FailedIdentifierItem,
} from 'shared/services/payment-links-by-identifiers.service';
import { IOptionSelect } from 'components/atoms/Select/Select';

export interface SuccessResult {
  sentIdentifiers: string[];
  failedIdentifiers: FailedIdentifierItem[];
  successfulIdentifiers: string[];
}

function parseIdentifiers(raw: string): string[] {
  const list = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set(list));
}

export function useSellInsuranceMassivelyIdentifier() {
  const navigate = useNavigate();
  const [identifiersRaw, setIdentifiersRaw] = useState('');
  const [keyCampaign, setKeyCampaign] = useState<string | number>('');
  const [campaignOptions, setCampaignOptions] = useState<IOptionSelect[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidIdentifiers, setInvalidIdentifiers] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [successResult, setSuccessResult] =
    useState<SuccessResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [campaignTouched, setCampaignTouched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingCampaigns(true);
    getEmailMarketingCampaigns()
      .then((data) => {
        if (cancelled) return;
        const options: IOptionSelect[] = Object.entries(data)
          .filter(([, item]) => item.status === true)
          .map(([key, item]) => ({
            value: key,
            description: item.title,
          }));
        setCampaignOptions(options);
      })
      .catch((err) => {
        if (!cancelled)
          setErrorMessage(
            err instanceof Error ? err.message : 'Error al cargar campañas'
          );
      })
      .finally(() => {
        if (!cancelled) setLoadingCampaigns(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = useCallback(async () => {
    setErrorMessage('');
    setInvalidIdentifiers([]);

    const list = parseIdentifiers(identifiersRaw);
    if (list.length === 0) {
      setErrorMessage('Ingresa al menos un identificador (hubspot id).');
      return;
    }

    const campaignKey =
      typeof keyCampaign === 'number' ? String(keyCampaign) : keyCampaign;
    if (!campaignKey.trim()) {
      setCampaignTouched(true);
      setErrorMessage('Selecciona una campaña.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await sendPaymentLinksByIdentifiers({
        identifiers: list,
        keyCampaign: campaignKey,
      });
      const failed = response.data?.failedIdentifiers ?? [];
      const failedSet = new Set(failed.map((f) => f.identifier));
      const successfulIdentifiers = list.filter((id) => !failedSet.has(id));

      setSuccessResult({
        sentIdentifiers: list,
        failedIdentifiers: failed,
        successfulIdentifiers,
      });
      setSuccessMessage(
        failed.length === 0
          ? 'Todos los identificadores se enviaron correctamente.'
          : `Proceso completado. ${successfulIdentifiers.length} enviado(s), ${failed.length} no enviado(s).`
      );
      setIdentifiersRaw('');
      setKeyCampaign('');
      setInvalidIdentifiers([]);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Error al enviar.');
    } finally {
      setIsSubmitting(false);
    }
  }, [identifiersRaw, keyCampaign]);

  const handleBack = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  const campaignError =
    campaignTouched && (keyCampaign === '' || keyCampaign === undefined)
      ? 'Selecciona una campaña'
      : undefined;

  const clearSuccessResult = useCallback(() => {
    setSuccessResult(null);
    setSuccessMessage('');
  }, []);

  return {
    identifiersRaw,
    setIdentifiersRaw,
    keyCampaign,
    setKeyCampaign,
    campaignOptions,
    loadingCampaigns,
    isSubmitting,
    invalidIdentifiers,
    successMessage,
    setSuccessMessage,
    successResult,
    clearSuccessResult,
    errorMessage,
    setErrorMessage,
    campaignTouched,
    setCampaignTouched,
    campaignError,
    handleSubmit,
    handleBack,
  };
}
